import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../utils/authUtils';

export interface JobApplication {
  id: string;
  user_id: string;
  job_id: string;
  optimization_id?: string;
  job_title: string;
  company_name: string;
  job_url?: string;
  location?: string;
  // Mirrors the DB CHECK constraint (see 20251004000001_add_not_applied_status.sql).
  // 'saved_for_later' is the draft state used when the agent has prepared
  // artifacts but the user has not yet hit Apply.
  application_status:
    | 'applied'
    | 'in_progress'
    | 'saved_for_later'
    | 'not_applied'
    | 'interviewing'
    | 'offered'
    | 'rejected'
    | 'accepted'
    | 'declined'
    | 'withdrawn';
  applied_date: string;
  last_updated: string;
  resume_version?: string;
  cover_letter_version?: string;
  application_notes?: string;
  cover_letter_snippet?: string;
  application_snippet?: string;
  follow_up_date?: string;
  reminder_date?: string;
  job_saved_date?: string;
  resume_version_id?: string;
  salary_range?: string;
  source_channel: 'linkedin' | 'naukri' | 'indeed' | 'glassdoor' | 'company_website' | 'referral' | 'other';
  confidence_score?: number;
  response_received: boolean;
  response_date?: string;
  created_at: string;
  updated_at: string;
}

export interface ApplicationStatistics {
  totalApplications: number;
  applicationsByStatus: { [status: string]: number };
  applicationsByChannel: { [channel: string]: number };
  responseRate: number;
  interviewRate: number;
  offerRate: number;
  averageResponseTime: number;
  monthlyTrends: { month: string; applications: number; responses: number }[];
}

export interface ApplicationFilters {
  status?: string;
  channel?: string;
  dateRange?: {
    start: string;
    end: string;
  };
  company?: string;
}

// Create a new job application
export async function createJobApplication(application: Omit<JobApplication, 'id' | 'created_at' | 'updated_at' | 'last_updated'>): Promise<JobApplication> {
  try {
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const applicationData = {
      ...application,
      user_id: user.id,
      applied_date: new Date().toISOString(),
      last_updated: new Date().toISOString(),
      response_received: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('job_applications')
      .insert(applicationData)
      .select()
      .single();

    if (error) {
      console.error('Γ¥î Error creating job application:', error);
      throw error;
    }

    console.log('Γ£à Job application created successfully:', data);
    return data;
  } catch (error) {
    console.error('Γ¥î Failed to create job application:', error);
    throw error;
  }
}

// Get all job applications for the current user
export async function getJobApplications(filters?: ApplicationFilters): Promise<JobApplication[]> {
  try {
    const user = await getCurrentUser();
    if (!user) {
      // Gracefully return empty — avoids console noise during session hydration
      return [];
    }

    // Check if this is an admin user (non-UUID ID indicates admin)
    const isAdminUser = !user.id.includes('-') || user.id.startsWith('admin-');
    
    let query = supabase
      .from('job_applications')
      .select('*')
      .order('applied_date', { ascending: false });
    
    // Only filter by user_id if not an admin user
    if (!isAdminUser) {
      query = query.eq('user_id', user.id);
    }

    // Apply filters
    if (filters?.status) {
      query = query.eq('application_status', filters.status);
    }
    
    if (filters?.channel) {
      query = query.eq('source_channel', filters.channel);
    }
    
    if (filters?.company) {
      query = query.ilike('company_name', `%${filters.company}%`);
    }
    
    if (filters?.dateRange) {
      query = query
        .gte('applied_date', filters.dateRange.start)
        .lte('applied_date', filters.dateRange.end);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Γ¥î Error fetching job applications:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Γ¥î Failed to fetch job applications:', error);
    return [];
  }
}

export interface JobApplicationsPage {
  data: JobApplication[];
  total: number;
}

// Get job applications with pagination (for My Activities / History table)
export async function getJobApplicationsPaginated(
  limit: number,
  offset: number,
  filters?: ApplicationFilters
): Promise<JobApplicationsPage> {
  try {
    const user = await getCurrentUser();
    if (!user) {
      return { data: [], total: 0 };
    }

    const isAdminUser = !user.id.includes('-') || user.id.startsWith('admin-');

    let query = supabase
      .from('job_applications')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (!isAdminUser) {
      query = query.eq('user_id', user.id);
    }
    if (filters?.status) {
      query = query.eq('application_status', filters.status);
    }
    if (filters?.channel) {
      query = query.eq('source_channel', filters.channel);
    }
    if (filters?.company) {
      query = query.ilike('company_name', `%${filters.company}%`);
    }
    if (filters?.dateRange) {
      query = query
        .gte('applied_date', filters.dateRange.start)
        .lte('applied_date', filters.dateRange.end);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching job applications (paginated):', error);
      throw error;
    }

    return { data: data || [], total: count ?? 0 };
  } catch (error) {
    console.error('Failed to fetch job applications (paginated):', error);
    return { data: [], total: 0 };
  }
}

// Update application status
export async function updateApplicationStatus(
  applicationId: string, 
  status: JobApplication['application_status'],
  notes?: string
): Promise<JobApplication> {
  try {
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const updateData: Partial<JobApplication> = {
      application_status: status,
      last_updated: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    // If status indicates a response, mark response_received as true
    if (['interview_scheduled', 'interview_completed', 'offered', 'rejected'].includes(status)) {
      updateData.response_received = true;
      updateData.response_date = new Date().toISOString();
    }

    // Add notes if provided
    if (notes) {
      updateData.application_notes = notes;
    }

    const { data, error } = await supabase
      .from('job_applications')
      .update(updateData)
      .eq('id', applicationId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      console.error('Γ¥î Error updating application status:', error);
      throw error;
    }

    console.log('Γ£à Application status updated successfully:', data);
    return data;
  } catch (error) {
    console.error('Γ¥î Failed to update application status:', error);
    throw error;
  }
}

// Get application statistics
export async function getApplicationStatistics(): Promise<ApplicationStatistics> {
  try {
    const user = await getCurrentUser();
    if (!user) {
      return {
        totalApplications: 0,
        applicationsByStatus: {},
        applicationsByChannel: {},
        responseRate: 0,
        interviewRate: 0,
        offerRate: 0,
        averageResponseTime: 0,
        monthlyTrends: [],
      };
    }

    // Check if this is an admin user (non-UUID ID indicates admin)
    const isAdminUser = !user.id.includes('-') || user.id.startsWith('admin-');
    
    let query = supabase
      .from('job_applications')
      .select('*');
    
    // Only filter by user_id if not an admin user
    if (!isAdminUser) {
      query = query.eq('user_id', user.id);
    }
    
    const { data: applications, error } = await query;

    if (error) {
      console.error('Γ¥î Error fetching applications for statistics:', error);
      throw error;
    }

    if (!applications || applications.length === 0) {
      return {
        totalApplications: 0,
        applicationsByStatus: {},
        applicationsByChannel: {},
        responseRate: 0,
        interviewRate: 0,
        offerRate: 0,
        averageResponseTime: 0,
        monthlyTrends: []
      };
    }

    // Calculate statistics
    const totalApplications = applications.length;
    
    // Status breakdown
    const applicationsByStatus: { [status: string]: number } = {};
    applications.forEach(app => {
      applicationsByStatus[app.application_status] = (applicationsByStatus[app.application_status] || 0) + 1;
    });

    // Channel breakdown
    const applicationsByChannel: { [channel: string]: number } = {};
    applications.forEach(app => {
      applicationsByChannel[app.source_channel] = (applicationsByChannel[app.source_channel] || 0) + 1;
    });

    // Response rate (applications that received any response)
    const responseRate = Math.round((applications.filter(app => app.response_received).length / totalApplications) * 100);

    // Interview rate (applications that reached interview stage)
    const interviewRate = Math.round((applications.filter(app => 
      ['interview_scheduled', 'interview_completed', 'offered'].includes(app.application_status)
    ).length / totalApplications) * 100);

    // Offer rate (applications that resulted in offers)
    const offerRate = Math.round((applications.filter(app => app.application_status === 'offered').length / totalApplications) * 100);

    // Average response time (for applications that received responses)
    const applicationsWithResponse = applications.filter(app => app.response_received && app.response_date);
    let averageResponseTime = 0;
    if (applicationsWithResponse.length > 0) {
      const totalResponseTime = applicationsWithResponse.reduce((sum, app) => {
        const appliedDate = new Date(app.applied_date);
        const responseDate = new Date(app.response_date!);
        return sum + (responseDate.getTime() - appliedDate.getTime());
      }, 0);
      averageResponseTime = Math.round(totalResponseTime / applicationsWithResponse.length / (1000 * 60 * 60 * 24)); // in days
    }

    // Monthly trends (last 6 months)
    const monthlyTrends = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthStr = month.toISOString().slice(0, 7); // YYYY-MM format
      
      const monthApplications = applications.filter(app => 
        app.applied_date.startsWith(monthStr)
      );
      
      const monthResponses = monthApplications.filter(app => 
        app.response_received && app.response_date && app.response_date.startsWith(monthStr)
      );

      monthlyTrends.push({
        month: month.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        applications: monthApplications.length,
        responses: monthResponses.length
      });
    }

    return {
      totalApplications,
      applicationsByStatus,
      applicationsByChannel,
      responseRate,
      interviewRate,
      offerRate,
      averageResponseTime,
      monthlyTrends
    };
  } catch (error) {
    console.error('Γ¥î Failed to get application statistics:', error);
    throw error;
  }
}

// Set up real-time subscription for application updates
export async function subscribeToApplicationUpdates(callback: (payload: any) => void) {
  const user = await getCurrentUser();
  
  if (!user) return null;

  const subscription = supabase
    .channel('application-updates')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'job_applications',
        filter: `user_id=eq.${user.id}`
      },
      callback
    )
    .subscribe();

  return subscription;
}

// Export applications to CSV
export function exportApplicationsToCSV(applications: JobApplication[]): string {
  const headers = [
    'Job Title',
    'Company',
    'Status',
    'Applied Date',
    'Last Updated',
    'Source Channel',
    'Response Received',
    'Response Date',
    'Notes'
  ];

  const csvContent = [
    headers.join(','),
    ...applications.map(app => [
      `"${app.job_title}"`,
      `"${app.company_name}"`,
      app.application_status,
      app.applied_date.split('T')[0], // Date only
      app.last_updated.split('T')[0],
      app.source_channel,
      app.response_received ? 'Yes' : 'No',
      app.response_date ? app.response_date.split('T')[0] : '',
      `"${app.application_notes || ''}"`
    ].join(','))
  ].join('\n');

  return csvContent;
}

// Schedule follow-up reminder
export async function scheduleFollowUp(
  applicationId: string,
  followUpDate: string,
  reminderNotes?: string
): Promise<JobApplication> {
  try {
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('job_applications')
      .update({
        follow_up_date: followUpDate,
        application_notes: reminderNotes,
        last_updated: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      console.error('Γ¥î Error scheduling follow-up:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Γ¥î Failed to schedule follow-up:', error);
    throw error;
  }
}

// Track application button click
export async function trackApplicationButtonClick(
  applicationId: string,
  jobUrl: string,
  method: 'direct_link' | 'copy_snippets' | 'save_for_later'
): Promise<void> {
  try {
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // For now, we'll track this in the application notes or create a simple log
    // In a full implementation, you'd want a separate tracking events table
    console.log(`≡ƒôè Application button clicked: ${applicationId}, method: ${method}, URL: ${jobUrl}`);
    
    // Update the application with tracking info
    await supabase
      .from('job_applications')
      .update({
        application_notes: `Button clicked via ${method} method. Job URL: ${jobUrl}`,
        last_updated: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .eq('user_id', user.id);

  } catch (error) {
    console.error('Γ¥î Failed to track application button click:', error);
    throw error;
  }
}

// Confirm application submission
export async function confirmApplicationSubmission(
  applicationId: string,
  submissionData: {
    submission_method: string;
    notes?: string;
  }
): Promise<JobApplication> {
  try {
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('job_applications')
      .update({
        application_status: 'applied',
        application_notes: `${submissionData.notes || 'Application confirmed'}. Method: ${submissionData.submission_method}`,
        response_received: false,
        last_updated: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      console.error('Γ¥î Error confirming application submission:', error);
      throw error;
    }

    console.log('Γ£à Application submission confirmed:', data);
    return data;
  } catch (error) {
    console.error('Γ¥î Failed to confirm application submission:', error);
    throw error;
  }
}

// Setup follow-up reminder
export async function setupFollowUpReminder(
  applicationId: string,
  followUpDate: string,
  reminderType: string = 'follow_up'
): Promise<JobApplication> {
  try {
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('job_applications')
      .update({
        follow_up_date: followUpDate,
        application_notes: `Follow-up reminder set for ${followUpDate}. Type: ${reminderType}`,
        last_updated: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      console.error('Γ¥î Error setting up follow-up reminder:', error);
      throw error;
    }

    console.log('Γ£à Follow-up reminder set up:', data);
    return data;
  } catch (error) {
    console.error('Γ¥î Failed to setup follow-up reminder:', error);
    throw error;
  }
}

/**
 * Save agentic artifacts (resume markdown, cover letter, application note) against a job
 * in `job_applications`, regardless of whether the user has clicked Apply yet.
 *
 * The `job_id` column is UUID-typed in the DB — when the job is from an external
 * source (LinkedIn / Naukri / Peerlist) the id is usually a non-UUID string, which
 * would cause a 22P02 cast error and the row would never persist. We strip it to
 * null in that case and record the original id in `application_notes` so we can
 * still recover the link.
 *
 * Lookup strategy:
 *   1. Try (user_id, job_id) when job_id is a UUID.
 *   2. Otherwise fall back to (user_id, job_title, company_name) to avoid duplicates.
 *
 * This is best-effort — failures are logged but never throw, so the agent flow
 * is never blocked by persistence issues.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isUuid(value: string | undefined | null): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

export async function saveAgentArtifacts(args: {
  jobId: string;
  jobTitle: string;
  companyName: string;
  jobUrl?: string;
  location?: string;
  sourceChannel?: JobApplication['source_channel'];
  resumeMarkdown?: string;
  coverLetter?: string;
  applicationNote?: string;
  matchScore?: number;
  optimizationId?: string;
  /**
   * When true, also flips the row's `application_status` to 'applied' and sets
   * `applied_date` to now. Used by the canvas "Apply Now" button so we get a
   * single round-trip that both records the application and persists the latest
   * artifacts.
   *
   * Prefer the more explicit `applicationStatus` field below for new code —
   * `markApplied: true` is equivalent to `applicationStatus: 'applied'` and is
   * kept only for backward compatibility.
   */
  markApplied?: boolean;
  /**
   * Explicit application status override. Use this for the Assisted Handoff
   * flow (set to `'in_progress'` the moment the user clicks Apply and the
   * portal opens — flip to `'applied'` only after the return-nudge
   * confirms the user actually submitted on the portal).
   *
   * When set, this takes precedence over `markApplied` and always stamps
   * `applied_date` (so we have a timestamp of when the user initiated
   * the application, useful for follow-up logic and dwell tracking).
   */
  applicationStatus?: 'applied' | 'in_progress' | 'saved_for_later' | 'not_applied';
}): Promise<JobApplication | null> {
  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('[saveAgentArtifacts] no authenticated user — skipping save');
      return null;
    }

    const nowIso = new Date().toISOString();
    const jobIdIsUuid = isUuid(args.jobId);
    // The DB column is uuid-typed — only pass the value if it's a real UUID.
    const safeJobId = jobIdIsUuid ? args.jobId : null;
    const externalIdNote = jobIdIsUuid ? '' : `[external_job_id: ${args.jobId}]`;

    // ── Step 1: locate any existing row ────────────────────────────────────────
    // First try by job_id (UUID match). If that fails or job_id is non-UUID,
    // fall back to (user_id, job_title, company_name) which uniquely identifies
    // an in-progress optimization for the same role.
    let existing: JobApplication | null = null;

    if (safeJobId) {
      const { data } = await supabase
        .from('job_applications')
        .select('*')
        .eq('user_id', user.id)
        .eq('job_id', safeJobId)
        .maybeSingle();
      existing = data ?? null;
    }

    if (!existing) {
      const { data } = await supabase
        .from('job_applications')
        .select('*')
        .eq('user_id', user.id)
        .eq('job_title', args.jobTitle)
        .eq('company_name', args.companyName)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      existing = data ?? null;
    }

    const noteParts: string[] = [];
    if (existing?.application_notes) noteParts.push(existing.application_notes);
    if (externalIdNote && !(existing?.application_notes ?? '').includes(externalIdNote)) {
      noteParts.push(externalIdNote);
    }
    const mergedNotes = noteParts.join(' ').trim() || undefined;

    const artifactPatch: Partial<JobApplication> = {
      job_title: args.jobTitle,
      company_name: args.companyName,
      job_url: args.jobUrl ?? existing?.job_url,
      location: args.location ?? existing?.location,
      source_channel: args.sourceChannel ?? existing?.source_channel ?? 'other',
      ...(mergedNotes ? { application_notes: mergedNotes } : {}),
      ...(args.resumeMarkdown ? { resume_version: args.resumeMarkdown.slice(0, 16000) } : {}),
      ...(args.coverLetter ? { cover_letter_snippet: args.coverLetter.slice(0, 8000) } : {}),
      ...(args.applicationNote ? { application_snippet: args.applicationNote.slice(0, 4000) } : {}),
      ...(args.matchScore !== undefined ? { confidence_score: args.matchScore } : {}),
      ...(args.optimizationId && isUuid(args.optimizationId)
        ? { optimization_id: args.optimizationId }
        : {}),
      ...(args.applicationStatus
        ? { application_status: args.applicationStatus, applied_date: nowIso }
        : args.markApplied
          ? { application_status: 'applied' as const, applied_date: nowIso }
          : {}),
      last_updated: nowIso,
      updated_at: nowIso,
    };

    if (existing) {
      const { data, error } = await supabase
        .from('job_applications')
        .update(artifactPatch)
        .eq('id', existing.id)
        .eq('user_id', user.id)
        .select()
        .single();
      if (error) {
        console.warn('[saveAgentArtifacts] update failed:', error.message, { row: existing.id });
        return null;
      }
      console.log('[saveAgentArtifacts] updated existing row', { id: data.id, jobTitle: args.jobTitle });
      return data;
    }

    // Insert a new draft row. We use 'saved_for_later' because it is the
    // only status in the DB CHECK constraint that means
    // "artifacts prepared but the user has not yet pressed Apply".
    // Using 'viewed' here previously triggered a 23514 check-constraint
    // violation and silently dropped every agent artifact.
    const insertRow = {
      user_id: user.id,
      job_id: safeJobId,
      application_status: 'saved_for_later' as const,
      applied_date: nowIso,
      response_received: false,
      created_at: nowIso,
      ...artifactPatch,
    };

    const { data, error } = await supabase
      .from('job_applications')
      .insert(insertRow)
      .select()
      .single();

    if (error) {
      console.warn('[saveAgentArtifacts] insert failed:', error.message, {
        jobId: args.jobId,
        jobIdIsUuid,
        title: args.jobTitle,
      });
      return null;
    }
    console.log('[saveAgentArtifacts] inserted new row', { id: data.id, jobTitle: args.jobTitle });
    return data;
  } catch (err) {
    console.warn('[saveAgentArtifacts] unexpected:', err);
    return null;
  }
}

// Create a default export object with all the methods
const ApplicationTrackingService = {
  trackApplicationButtonClick,
  confirmApplicationSubmission,
  setupFollowUpReminder,
  createJobApplication,
  getJobApplications,
  updateApplicationStatus,
  getApplicationStatistics,
  subscribeToApplicationUpdates,
  exportApplicationsToCSV,
  scheduleFollowUp,
  saveAgentArtifacts
};

export default ApplicationTrackingService;
